import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Share, PlusSquare, CheckCircle, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const BETA_WELCOME_KEY = 'twintube_beta_welcome_seen';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if running as installed PWA on iOS
    if (window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Don't show if dismissed recently (within 24 hours)
    const dismissedTime = localStorage.getItem('pwaPromptDismissedTime');
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return;
      }
    }

    // Wait for beta welcome to be seen/closed before showing install prompt
    const showPromptIfReady = () => {
      const hasSeenBetaWelcome = localStorage.getItem(BETA_WELCOME_KEY);
      if (hasSeenBetaWelcome) {
        // For iOS - show manual install instructions after a delay
        if (isIOSDevice && !window.navigator.standalone) {
          setShowPrompt(true);
        }
      } else {
        // Check again after a delay (beta popup shows at 1.5s)
        setTimeout(showPromptIfReady, 2000);
      }
    };

    // Start checking after 4 seconds (after beta popup would have appeared)
    setTimeout(showPromptIfReady, 4000);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredInstallPrompt = e;
      // Also wait for beta welcome on Android
      const hasSeenBetaWelcome = localStorage.getItem(BETA_WELCOME_KEY);
      if (hasSeenBetaWelcome) {
        setShowPrompt(true);
      } else {
        setTimeout(() => setShowPrompt(true), 4000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setShowSuccessMessage(true);
      
      // Record the install to backend
      const platform = isIOSDevice ? 'ios' : isAndroidDevice ? 'android' : 'desktop';
      axios.post(`${API}/stats/install`, null, { params: { platform } })
        .then(() => console.log('Install tracked'))
        .catch(err => console.error('Failed to track install:', err));
      
      // Show toast notification
      toast.success('Twin Tube installed successfully!', {
        description: 'Find it on your home screen. Enjoy the music!',
        duration: 5000,
        icon: <PartyPopper className="text-purple-400" />,
      });
      
      // Hide success message after 6 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 6000);
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      window.deferredInstallPrompt = null;
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if prompt already available
    if (window.deferredInstallPrompt) {
      setDeferredPrompt(window.deferredInstallPrompt);
      setShowPrompt(true);
    }

    // Also listen for custom event
    const handleInstallAvailable = () => {
      setDeferredPrompt(window.deferredInstallPrompt);
      setShowPrompt(true);
    };
    window.addEventListener('pwaInstallAvailable', handleInstallAvailable);

    // Show prompt after delay if on mobile and not installed
    if ((isIOSDevice || isAndroidDevice) && !window.matchMedia('(display-mode: standalone)').matches) {
      setTimeout(() => {
        if (!isInstalled) {
          setShowPrompt(true);
        }
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('pwaInstallAvailable', handleInstallAvailable);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    // First try our saved deferred prompt
    const promptToUse = deferredPrompt || window.deferredInstallPrompt;
    
    if (!promptToUse) {
      // No deferred prompt available - show manual instructions
      if (isIOS) {
        // iOS doesn't support beforeinstallprompt
        return;
      }
      
      // Show manual install instructions for Android
      toast.info(
        <div className="text-left">
          <p className="font-bold mb-2">To install Twin Tube:</p>
          <ol className="text-sm space-y-1">
            <li>1. Tap the <strong>⋮</strong> menu (3 dots at top)</li>
            <li>2. Select <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></li>
          </ol>
        </div>,
        { duration: 8000 }
      );
      return;
    }

    setIsInstalling(true);
    
    try {
      promptToUse.prompt();
      const { outcome } = await promptToUse.userChoice;
      
      if (outcome === 'accepted') {
        // Show immediate feedback while install completes
        toast.loading('Installing Twin Tube...', {
          id: 'installing',
          duration: 2000,
        });
        
        setIsInstalled(true);
        setShowPrompt(false);
        
        // The 'appinstalled' event will fire and show the success message
        // But also show success after a short delay as backup
        setTimeout(() => {
          toast.dismiss('installing');
          if (!showSuccessMessage) {
            setShowSuccessMessage(true);
            toast.success('Twin Tube installed!', {
              description: 'Check your home screen to open the app.',
              duration: 5000,
              icon: <CheckCircle className="text-green-400" />,
            });
            setTimeout(() => setShowSuccessMessage(false), 6000);
          }
        }, 1500);
      } else {
        toast.info('Installation cancelled', {
          description: 'You can install anytime from the browser menu.',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Install error:', error);
      toast.error('Installation failed', {
        description: 'Please try again or use your browser menu to install.',
        duration: 4000,
      });
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
      window.deferredInstallPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaPromptDismissedTime', Date.now().toString());
  };

  // Success message overlay
  if (showSuccessMessage) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className="relative overflow-hidden border border-purple-500/50 rounded-2xl max-w-sm w-full text-center shadow-2xl"
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-30"
              style={{ backgroundImage: 'url(/images/twin-tube-bg.jpg)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-purple-900/90 via-gray-900/95 to-gray-900" />
            
            {/* Content */}
            <div className="relative p-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center"
              >
                <CheckCircle size={40} className="text-green-400" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-white mb-2"
              >
                Installation Complete! 🎉
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-gray-300 mb-6"
            >
              Twin Tube has been added to your home screen. Open it anytime for instant access to Twin Cities music!
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="space-y-3"
            >
              <div className="flex items-center justify-center gap-2 text-sm text-purple-300">
                <Smartphone size={16} />
                <span>Find the app on your home screen</span>
              </div>
              
              <Button
                onClick={() => setShowSuccessMessage(false)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                Got it!
              </Button>
            </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-24 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        data-testid="install-prompt"
      >
        <div className="bg-gradient-to-br from-purple-900/95 to-gray-900/95 backdrop-blur-lg border border-purple-500/30 rounded-xl p-5 shadow-2xl">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
            data-testid="dismiss-install"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-purple-500/20 border border-purple-500/30">
              <Smartphone size={28} className="text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-white mb-1">Install Twin Tube</h3>
              <p className="text-sm text-gray-300 mb-4">
                Get the full experience - instant access, works offline, and music keeps playing!
              </p>
              
              {isIOS ? (
                // iOS Instructions
                <div className="space-y-3">
                  <p className="text-xs text-gray-400 mb-2">To install on iPhone/iPad:</p>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs">1</div>
                    <span>Tap the</span>
                    <Share size={16} className="text-blue-400" />
                    <span>Share button</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs">2</div>
                    <span>Scroll and tap</span>
                    <PlusSquare size={16} className="text-blue-400" />
                    <span>&quot;Add to Home Screen&quot;</span>
                  </div>
                  <Button
                    onClick={handleDismiss}
                    size="sm"
                    variant="outline"
                    className="w-full mt-2 border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                  >
                    Got it!
                  </Button>
                </div>
              ) : (
                // Android/Chrome Install Button
                <Button
                  onClick={deferredPrompt ? handleInstall : handleDismiss}
                  size="sm"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
                  data-testid="install-btn"
                  disabled={isInstalling}
                >
                  {isInstalling ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Installing...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      {deferredPrompt ? 'Install App' : 'Learn More'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
